<script setup lang="ts">
import type { OnPaymentCompletedData } from "@adyen/adyen-web/dist/types/components/types";
import { useQueryClient } from "@tanstack/vue-query";

import { queries } from "~/queries";
import { useCreatePaymentSessionForTokenisationMutation } from "~/queries/finance/tokens";
import type { PaymentCompleted } from "~/types/Payment";

const i18n = useI18n();
const toast = useToast();
const route = useRoute();
const queryClient = useQueryClient();
const localePath = useLocalePath();
const { sessionId } = route.query;
const session = ref();
const { cardTokenizationFailed } = useAnalytics();
const addCardFailureMessage = i18n.t("myAccount.payment.addCardFailure");

const { redirectPath } = defineProps<{ redirectPath?: string }>();

const emit = defineEmits<{ (e: "tokenisation-success"): void }>();

const error = computed(() => route.query?.error);
const paymentErrorReason = ref();
const { mutateAsync: createPaymentSessionForTokenisation } =
  useCreatePaymentSessionForTokenisationMutation();

const initializePaymentSession = async () => {
  try {
    const response = await createPaymentSessionForTokenisation({
      returnUrl: `${window.location.href.split("?")[0]}`,
      localeCode: i18n.locale.value,
    });
    session.value = {
      id: response.id,
      sessionData: response.sessionData,
    };
  } catch (e) {
    toast.error(getErrorMessage(e)[0]);
  }
};

watchEffect(() => {
  if (error.value) {
    paymentErrorReason.value = error.value;
  }
});

onMounted(async () => {
  if (sessionId) {
    session.value = { id: sessionId };
  } else {
    await initializePaymentSession();
  }
});

const onPaymentCompleted = async (
  result: OnPaymentCompletedData | PaymentCompleted,
) => {
  const refusalReasonCode = (result as PaymentCompleted).refusalReasonCode;
  if (refusalReasonCode !== null && refusalReasonCode !== undefined) {
    paymentErrorReason.value = refusalReasonCode;
    toast.error(addCardFailureMessage);
    window.scrollTo(0, 0);

    return;
  }
  paymentErrorReason.value = undefined;
  toast.success(i18n.t("myAccount.payment.addCardSuccess"));

  queryClient.invalidateQueries({
    queryKey: queries.tokens.list().queryKey,
  });

  emit("tokenisation-success");
  if (redirectPath) return await navigateTo(localePath(redirectPath));

  return await navigateTo(localePath("/my-account/payment"));
};

const logSegmentEvent = (refusalReasonCode: string) => {
  cardTokenizationFailed({
    tokenization_failed_reason: refusalReasonCode,
  });
};

const onPaymentFailed = async (error?: Error) => {
  if (error) {
    logSegmentEvent(error.message);
    toast.error(getErrorMessage(error)[0]);
  } else {
    logSegmentEvent("addCardFailure");
    toast.error(addCardFailureMessage);
  }
  await initializePaymentSession();
};
</script>

<template>
  <CheckoutPaymentFailedNotification
    v-if="paymentErrorReason !== undefined"
    class="mt-4"
    :refusal-reason-code="paymentErrorReason"
  />
  <UserAccountPaymentTokenisationAdyenDropin
    :redirect-url="redirectPath"
    :session="session"
    @payment-failed="onPaymentFailed"
    @payment-completed="onPaymentCompleted"
  />
  <p class="text-platform-text-3 text-xs">
    {{ $t("product.cardReservationMessage.securePayment") }}
  </p>
</template>
